import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/adminApi';
import { consoleApi } from '@/lib/consoleApi';
import { aggregateCatalogue, cohortConcordance } from '@/lib/periReview';
import {
  findingsMarkdown, openFindings, openP0, sortFindings,
  FINDING_CATEGORIES, FINDING_SEVERITIES, FINDING_SOURCES, FINDING_STATUSES, SEVERITY_CLS,
  type BetaFinding, type FindingCategory, type FindingSeverity, type FindingSource, type FindingStatus,
} from '@/lib/betaConsole';
import { LoadingNote, ErrorNote, EmptyNote } from '@/admin';

function download(text: string, name: string, type: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type }));
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** The findings log. Everything the beta produces lands here, categorised, so the
 *  disagreement catalogue and the backlog are the same artifact.
 *
 *  The Markdown export MERGES this log with the disagreement catalogue — but the
 *  catalogue rows come from the peri-review queue, where the server already
 *  derived them from signed verdicts. We do not re-derive them here; there must
 *  be exactly one derivation of what counts as a disagreement. */
export function FindingsTab({ canEdit, blockingGates }: { canEdit: boolean; blockingGates: number }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['beta-findings'], queryFn: () => adminApi.beta.findings.list() });
  const queue = useQuery({ queryKey: ['peri-review-queue'], queryFn: () => consoleApi.periReview.queue() });

  const [cat, setCat] = useState<FindingCategory | ''>('');
  const [severity, setSeverity] = useState<FindingSeverity>('P2');
  const [src, setSrc] = useState<FindingSource | ''>('');
  const [desc, setDesc] = useState('');
  const [error, setError] = useState('');

  const create = useMutation({
    mutationFn: () => adminApi.beta.findings.create({ cat: cat as FindingCategory, severity, src: src as FindingSource, desc }),
    onSuccess: () => {
      setCat(''); setSrc(''); setDesc(''); setSeverity('P2'); setError('');
      void qc.invalidateQueries({ queryKey: ['beta-findings'] });
    },
  });
  const patch = useMutation({
    mutationFn: ({ id, status }: { id: string; status: FindingStatus }) => adminApi.beta.findings.patch(id, { status }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['beta-findings'] }); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => adminApi.beta.findings.remove(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['beta-findings'] }); },
  });

  if (q.isLoading) return <LoadingNote />;
  if (q.isError) {
    return (
      <ErrorNote>
        Could not load findings. The <code>/admin/beta/findings</code> endpoint is owned by
        beta-console-backend and may not be deployed here yet.
      </ErrorNote>
    );
  }

  const findings = q.data ?? [];
  const rows = queue.data?.participants ?? [];

  function onExport() {
    download(
      findingsMarkdown({
        findings,
        catalogue: aggregateCatalogue(rows),
        concordance: cohortConcordance(rows),
        blockingGates,
        today: new Date().toISOString().slice(0, 10),
      }),
      `peri_beta_findings_${new Date().toISOString().slice(0, 10)}.md`,
      'text/markdown',
    );
  }

  function onSubmit() {
    if (!cat || !src || !desc.trim()) {
      setError('Category, source and description are all required. A finding without a category is a feeling.');
      return;
    }
    create.mutate();
  }

  return (
    <>
      {canEdit ? (
        <section className="mb-13 rounded border border-border bg-surface p-13">
          <h2 className="mb-2 text-body font-semibold text-text-primary">Log a finding</h2>
          <p className="mb-2 text-body-sm text-text-muted">
            Everything the beta produces lands here, categorised, so the disagreement catalogue and
            the backlog are the same artifact.
          </p>
          <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <label className="text-[11px] font-semibold uppercase text-text-muted">
              Category
              <select className="searchbox !mb-0 mt-1" value={cat} onChange={(e) => setCat(e.target.value as FindingCategory)}>
                <option value="">—</option>
                {FINDING_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="text-[11px] font-semibold uppercase text-text-muted">
              Severity
              <select className="searchbox !mb-0 mt-1" value={severity} onChange={(e) => setSeverity(e.target.value as FindingSeverity)}>
                {FINDING_SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="text-[11px] font-semibold uppercase text-text-muted">
              Source
              <select className="searchbox !mb-0 mt-1" value={src} onChange={(e) => setSrc(e.target.value as FindingSource)}>
                <option value="">—</option>
                {FINDING_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <label className="block text-[11px] font-semibold uppercase text-text-muted">
            What happened
            <textarea
              className="searchbox !mb-0 mt-1 min-h-[60px]"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Specific enough that a developer could act on it without asking you a question"
            />
          </label>
          {error ? <p className="mt-1 text-body-sm text-flagged">{error}</p> : null}
          <button type="button" className="btn mt-2" onClick={onSubmit} disabled={create.isPending}>
            Log finding
          </button>
        </section>
      ) : null}

      <section className="mb-13 rounded border border-border bg-surface p-13">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-body font-semibold text-text-primary">
            Log{' '}
            <span className="text-body-sm font-normal text-text-muted">
              · {openFindings(findings).length} open of {findings.length}
              {openP0(findings).length ? ` · ${openP0(findings).length} P0` : ''}
            </span>
          </h2>
          <button type="button" className="btn" onClick={onExport}>Export findings as Markdown</button>
        </div>

        {findings.length === 0 ? (
          <EmptyNote>Nothing logged yet.</EmptyNote>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th className="w-24">Date</th>
                  <th className="w-14">Sev</th>
                  <th className="w-32">Category</th>
                  <th className="w-32">Source</th>
                  <th className="w-24">Participant</th>
                  <th>Finding</th>
                  <th className="w-32">Status</th>
                  {canEdit ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {sortFindings(findings).map((f: BetaFinding) => (
                  <tr key={f.id}>
                    <td className="align-top font-mono text-[11px]">{f.date}</td>
                    <td className="align-top">
                      <span className={`inline-block rounded-pill px-2 py-0.5 text-[11px] font-semibold ${SEVERITY_CLS[f.severity]}`}>
                        {f.severity}
                      </span>
                    </td>
                    <td className="align-top">{f.cat}</td>
                    <td className="align-top text-text-secondary">{f.src}</td>
                    <td className="align-top font-mono text-[11px]">{f.participant_ref ?? '—'}</td>
                    <td className="align-top text-text-secondary">{f.desc}</td>
                    <td className="align-top">
                      {canEdit ? (
                        <select
                          className="searchbox !mb-0 !w-auto"
                          aria-label={`Status — ${f.desc.slice(0, 40)}`}
                          value={f.status}
                          onChange={(e) => patch.mutate({ id: f.id, status: e.target.value as FindingStatus })}
                        >
                          {FINDING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : (
                        <span className="text-text-secondary">{f.status}</span>
                      )}
                    </td>
                    {canEdit ? (
                      <td className="align-top">
                        <button type="button" className="btn" onClick={() => remove.mutate(f.id)}>Remove</button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
